import { useMemo } from 'react';

import { Box } from '@mui/system';
import { isNumber } from 'lodash-es';
import * as Yup from 'yup';

import { FormPage } from 'components/page';
import { FormattedStore, useApplicationContext } from 'context/ApplicationContext';
import { useFormContext } from 'context/FormContext';
import { useLoaderContext } from 'context/LoaderContext';
import { ConfiguracaoGeral } from 'pages/revendedor/configuracao-geral/types';
import {
    RevendedorAprovadoToggle,
    RevendedorAtivoToggle,
    RevendedorAutoCompletesBlock,
    RevendedorDiscountBlock,
    RevendedorFilesBlock,
    RevendedorGeneralBlock,
} from 'pages/revendedor/revendedor/components';
import { useMutateRevendedor, useQueryConfigGeral } from 'pages/revendedor/revendedor/hooks';
import { Revendedor } from 'pages/revendedor/revendedor/types';

/**
 * Prepara valores iniciais para NOVO cadastro (sem content)
 */
const prepareNewRevendedor = (configGeral: ConfiguracaoGeral | undefined, formattedStore: FormattedStore | null) => ({
    vlMinimoDesconto: configGeral?.vlMinimoDesconto,
    peDesconto: configGeral?.peDesconto,
    loja: formattedStore,
});

/**
 * Prepara valores iniciais para EDIÇÃO (com content)
 */
const prepareEditRevendedor = (data: Revendedor, configGeral: ConfiguracaoGeral | undefined) => {
    if (data.dtNascimento && !data.dtNascimento?.endsWith('T00:00:00')) {
        data.dtNascimento = `${data.dtNascimento}T00:00:00`;
    }

    if (!isNumber(data?.vlMinimoDesconto) && configGeral) {
        data.vlMinimoDesconto = configGeral.vlMinimoDesconto;
    }

    if (!isNumber(data?.peDesconto) && configGeral) {
        data.peDesconto = configGeral.peDesconto;
    }

    return data;
};

export function RevendedorFormComp() {
    const { handleSubmit, isLoading } = useMutateRevendedor();
    const { data: configGeral } = useQueryConfigGeral();
    const { content } = useFormContext();
    const { formattedStore } = useApplicationContext();

    useLoaderContext(isLoading);

    const validationSchema = Yup.object({
        dsNome: Yup.string().required(),
        tipoRevendedor: Yup.object().required(),
        dsEmail: Yup.string().email().required(),
        dsTelefone: Yup.string().required(),
        cep: Yup.object()
            .shape({
                nrCep: Yup.string().length(8, 'O campo deve ter exatamente 8 números.').required(),
                estado: Yup.object().required(),
                cidade: Yup.object().required(),
            })
            .required(),
        loja: Yup.object().required(),
        dsDocumento: Yup.string()
            .required()
            .when('tipoRevendedor', ([tipo], schema) => {
                if (tipo?.tipoRevendedorEnum === 'PF') {
                    return schema.length(11, 'O campo deve ter exatamente 11 números (CPF).');
                }
                if (tipo?.tipoRevendedorEnum === 'PJ') {
                    return schema.length(14, 'O campo deve ter exatamente 14 números (CNPJ).');
                }

                return schema;
            }),
    });

    const initialValues = useMemo(() => {
        if (!content) return prepareNewRevendedor(configGeral, formattedStore);

        return prepareEditRevendedor(content, configGeral);
    }, [configGeral, content, formattedStore]);

    return (
        <FormPage
            goBackButton
            title="Revendedor"
            url="/gestao/revendedorwrapper"
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            values={initialValues}
            extraContent={
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <RevendedorAtivoToggle />
                    <RevendedorAprovadoToggle />
                </Box>
            }
        >
            <RevendedorGeneralBlock />

            <RevendedorDiscountBlock />

            <RevendedorAutoCompletesBlock />

            <RevendedorFilesBlock />
        </FormPage>
    );
}
